import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { History, ShieldAlert, RefreshCw, Loader2, ArrowRight, Download, LogIn, LogOut, MapPin, MapPinOff, AlertTriangle, CheckCircle2, Smartphone, Mail, XCircle, MinusCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { fmtLogFull } from "@/lib/tz";

const CRIMSON = "var(--brand)";

const ACTION_STYLE = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-amber-50 text-amber-700 border-amber-200",
  delete: "bg-rose-50 text-rose-700 border-rose-200",
};

const fmt = (iso) => (iso ? fmtLogFull(iso) : "");

// Safely extract a display name for the actor field, which in older production
// records may have been stored as a {username, name} object rather than a string.
const actorLabel = (r) => {
  if (!r) return "—";
  if (r.actor_name) return r.actor_name;
  if (r.actor && typeof r.actor === "object") return r.actor.name || r.actor.username || "—";
  return r.actor || "—";
};

function valStr(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

// Convert a snake_case or camelCase DB field name into a human-readable label.
function fieldLabel(field) {
  if (!field) return field;
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → spaced
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// Strip HTML tags and decode HTML entities to get plain readable text.
function htmlToText(str) {
  if (typeof str !== "string") return str;
  if (!/<[a-z][\s\S]*>/i.test(str)) return str;
  try {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return doc.body.textContent || "";
  } catch {
    return str.replace(/<[^>]+>/g, "");
  }
}

// Human-friendly value: strips HTML, pretty-prints objects/arrays.
function humanVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    return v
      .map((item, idx) => {
        const inner =
          item !== null && typeof item === "object"
            ? Object.entries(item)
                .map(([k, val]) => `  ${fieldLabel(k)}: ${humanVal(val)}`)
                .join("\n")
            : String(item);
        return `[${idx + 1}]\n${inner}`;
      })
      .join("\n\n");
  }
  if (typeof v === "object") {
    try {
      const entries = Object.entries(v);
      if (entries.length === 0) return "—";
      return entries.map(([k, val]) => `${fieldLabel(k)}: ${humanVal(val)}`).join("\n");
    } catch {
      return JSON.stringify(v, null, 2);
    }
  }
  const s = String(v);
  const plain = htmlToText(s);
  return plain.replace(/\s+/g, " ").trim() || "—";
}

// A before/after cell that shows human-friendly text first, with a "Show raw" toggle.
function AuditCell({ value, colorClass, bgClass }) {
  const [showRaw, setShowRaw] = useState(false);
  const human = humanVal(value);
  const raw = valStr(value);
  const isHtml = typeof value === "string" && /<[a-z][\s\S]*>/i.test(value);
  const isObj = value !== null && value !== undefined && typeof value === "object";
  const canToggle = isHtml || isObj;

  return (
    <div className={`rounded px-1.5 py-1 ${bgClass}`}>
      <div className="max-h-48 overflow-y-auto">
        <p className={`whitespace-pre-wrap break-words text-[11px] leading-relaxed ${colorClass}`}>{human}</p>
      </div>
      {canToggle && (
        <button
          onClick={() => setShowRaw((s) => !s)}
          className="mt-1 text-[10px] text-slate-400 hover:text-slate-600 underline underline-offset-2"
        >
          {showRaw ? "Hide raw" : "Show raw"}
        </button>
      )}
      {canToggle && showRaw && (
        <div className="max-h-48 overflow-y-auto mt-1">
          <pre className={`whitespace-pre-wrap break-all font-mono text-[10px] opacity-70 ${colorClass}`}>{raw}</pre>
        </div>
      )}
    </div>
  );
}

export default function AuditLog() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "all", doctype: "", docname: "", actor: "", date_from: "", date_to: "" });
  // Draft holds the filter values as the user types; filters holds the last applied values.
  const [draft, setDraft] = useState({ action: "all", doctype: "", docname: "", actor: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState("activity");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filters.action !== "all") params.action = filters.action;
      if (filters.doctype) params.doctype = filters.doctype;
      if (filters.docname) params.docname = filters.docname;
      if (filters.actor) params.actor = filters.actor;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const data = await api.auditLog(params);
      setRows(data.entries || []);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Apply the current draft values as the active filters (triggers a re-fetch via load).
  const applyFilters = useCallback(() => {
    setFilters({ ...draft });
  }, [draft]);

  // Clear all filters back to defaults.
  const clearFilters = useCallback(() => {
    const empty = { action: "all", doctype: "", docname: "", actor: "", date_from: "", date_to: "" };
    setDraft(empty);
    setFilters(empty);
  }, []);

  // Allow pressing Enter inside any text input to apply.
  const onKeyDown = (e) => { if (e.key === "Enter") applyFilters(); };

  const openDetail = async (r) => {
    if (!r.id) { setDetail({ ...r, changes: [], _noid: true }); return; }
    setDetail(r); setDetailLoading(true);
    try { setDetail(await api.auditEntry(r.id)); }
    catch { setDetail({ ...r, changes: [], _err: true }); }
    finally { setDetailLoading(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="audit-log-page">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <History className="h-5 w-5" style={{ color: CRIMSON }} />
          <h1 className="text-xl font-semibold font-brand text-slate-800">Audit Log</h1>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Everything that happens across the system — who did what, when, and exactly what changed. Click any row to see the before/after. Deleted documents are kept here with downloadable copies of what was removed.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4" data-testid="audit-tabs">
          <TabsTrigger value="activity" data-testid="audit-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="deletions" data-testid="audit-tab-deletions">Document deletions</TabsTrigger>
          <TabsTrigger value="clock" data-testid="audit-tab-clock">Clock &amp; errors</TabsTrigger>
          <TabsTrigger value="emails" data-testid="audit-tab-emails">Emails sent</TabsTrigger>
          {can.has("system.security_log") && (
            <TabsTrigger value="alerts" data-testid="audit-tab-alerts">Alerts</TabsTrigger>
          )}
          {can.has("system.security_log") && (
            <TabsTrigger value="security" data-testid="audit-tab-security">Security</TabsTrigger>
          )}
          {can.has("system.security_log") && (
            <TabsTrigger value="access" data-testid="audit-tab-access">Record access</TabsTrigger>
          )}
          {can.has("system.security_log") && (
            <TabsTrigger value="writes" data-testid="audit-tab-writes">Writes</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="activity">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh-btn" className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
      {/* Filters */}
      <div className="mb-4 p-3 rounded-lg bg-white border border-slate-200 space-y-3">
        {/* Row 1: Action + Record type + Record ID + User */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Action</label>
            <Select value={draft.action} onValueChange={(v) => setDraft((f) => ({ ...f, action: v }))}>
              <SelectTrigger data-testid="audit-filter-action" className="h-9 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Record type</label>
            <Input data-testid="audit-filter-doctype" className="h-9 mt-1" placeholder="e.g. Roster Shift"
                   value={draft.doctype} onKeyDown={onKeyDown}
                   onChange={(e) => setDraft((f) => ({ ...f, doctype: e.target.value }))} />
          </div>
          <div className="w-48">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Record ID</label>
            <Input data-testid="audit-filter-docname" className="h-9 mt-1" placeholder="e.g. SH-00123, EMP-007"
                   value={draft.docname} onKeyDown={onKeyDown}
                   onChange={(e) => setDraft((f) => ({ ...f, docname: e.target.value }))} />
          </div>
          <div className="w-44">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">User</label>
            <Input data-testid="audit-filter-actor" className="h-9 mt-1" placeholder="partial username / email"
                   value={draft.actor} onKeyDown={onKeyDown}
                   onChange={(e) => setDraft((f) => ({ ...f, actor: e.target.value }))} />
          </div>
        </div>
        {/* Row 2: Date range + Apply / Clear */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</label>
            <Input type="date" data-testid="audit-filter-from" className="h-9 mt-1"
                   value={draft.date_from} onChange={(e) => setDraft((f) => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div className="w-40">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</label>
            <Input type="date" data-testid="audit-filter-to" className="h-9 mt-1"
                   value={draft.date_to} onChange={(e) => setDraft((f) => ({ ...f, date_to: e.target.value }))} />
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="audit-clear-btn" className="gap-1.5">
              Clear
            </Button>
            <Button size="sm" onClick={applyFilters} data-testid="audit-apply-btn" className="gap-1.5" style={{ backgroundColor: CRIMSON, color: "#fff" }}>
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <th className="text-left font-semibold px-4 py-2.5">When</th>
              <th className="text-left font-semibold px-4 py-2.5">User</th>
              <th className="text-left font-semibold px-4 py-2.5">Action</th>
              <th className="text-left font-semibold px-4 py-2.5">Record type</th>
              <th className="text-left font-semibold px-4 py-2.5">Record</th>
              <th className="text-left font-semibold px-4 py-2.5">Changes</th>
            </tr>
          </thead>
          <tbody data-testid="audit-rows">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="audit-empty">No audit entries match these filters.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id || i} onClick={() => openDetail(r)} className="border-b border-slate-100 hover:bg-amber-50/50 cursor-pointer" data-testid="audit-row">
                <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmt(r.timestamp)}</td>
                <td className="px-4 py-2.5 text-slate-800 font-medium">{actorLabel(r)}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${ACTION_STYLE[r.action] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{r.action}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{r.doctype}</td>
                <td className="px-4 py-2.5 text-slate-500 font-mono text-[11px]">{r.docname}</td>
                <td className="px-4 py-2.5 text-slate-500">
                  {r.change_count > 0
                    ? <span className="text-slate-600">{r.change_count} field{r.change_count === 1 ? "" : "s"}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && rows.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-400">Showing {rows.length} most recent entries.</div>
      )}
        </TabsContent>

        <TabsContent value="deletions">
          <DocumentDeletions active={tab === "deletions"} />
        </TabsContent>

        <TabsContent value="clock">
          <ClockEvents active={tab === "clock"} />
        </TabsContent>

        <TabsContent value="emails">
          <EmailsSent active={tab === "emails"} />
        </TabsContent>

        {can.has("system.security_log") && (
          <TabsContent value="alerts">
            <AlertsPanel active={tab === "alerts"} />
          </TabsContent>
        )}

        {can.has("system.security_log") && (
          <TabsContent value="security">
            <SecurityEvents active={tab === "security"} />
          </TabsContent>
        )}

        {can.has("system.security_log") && (
          <TabsContent value="access">
            <RecordAccess active={tab === "access"} />
          </TabsContent>
        )}

        {can.has("system.security_log") && (
          <TabsContent value="writes">
            <WriteReceipts active={tab === "writes"} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="sm:max-w-5xl" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-brand flex items-center gap-2">
              <History className="h-4 w-4" style={{ color: CRIMSON }} />
              {detail && <span className="capitalize">{detail.action}</span>}
              <span className="text-slate-400 font-normal">·</span>
              <span className="text-slate-700">{detail?.doctype}</span>
              {detail?.docname && <span className="font-mono text-xs text-slate-400">{detail.docname}</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div>
              <div className="text-[11px] text-slate-500 mb-3">
                By <span className="font-medium text-slate-700">{actorLabel(detail)}</span> · {fmt(detail.timestamp)}
              </div>
              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
              ) : detail._noid || detail._err ? (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
                  Field-level detail isn't available for this entry (it predates change tracking).
                </div>
              ) : (Array.isArray(detail.changes) ? detail.changes : []).length === 0 ? (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
                  No field-level changes were recorded for this action.
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-auto rounded-lg border border-slate-200" data-testid="audit-changes">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-slate-500 border-b border-slate-200">
                        <th className="text-left font-semibold px-3 py-2 w-1/4">Field</th>
                        <th className="text-left font-semibold px-3 py-2">Before</th>
                        <th className="w-6"></th>
                        <th className="text-left font-semibold px-3 py-2">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(detail.changes) ? detail.changes : []).map((c, i) => (
                        <tr key={i} className="border-b border-slate-100 align-top" data-testid={`audit-change-${c.field}`}>
                          <td className="px-3 py-2 break-all">
                            <span className="font-medium text-slate-700">{fieldLabel(c.field)}</span>
                            <span className="block font-mono text-[10px] text-slate-400 mt-0.5">{c.field}</span>
                          </td>
                          <td className="px-3 py-2">
                            <AuditCell value={c.before} colorClass="text-rose-700" bgClass="bg-rose-50/60" />
                          </td>
                          <td className="px-1 py-2 text-slate-300"><ArrowRight className="h-3.5 w-3.5" /></td>
                          <td className="px-3 py-2">
                            <AuditCell value={c.after} colorClass="text-emerald-700" bgClass="bg-emerald-50/60" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const VARIANT_LABEL = { original: "Original PDF", office: "Office PDF", worker: "Worker PDF", json: "Snapshot (JSON)" };

function DocumentDeletions({ active }) {
  const [rows, setRows] = useState(null);
  const [dl, setDl] = useState("");

  const load = useCallback(() => {
    setRows(null);
    api.documentDeletions().then(setRows).catch(() => { setRows([]); toast.error("Could not load deleted documents."); });
  }, []);

  useEffect(() => { if (active && rows === null) load(); }, [active, rows, load]);

  const download = async (e, variant) => {
    const key = `${e.id}-${variant}`;
    setDl(key);
    try {
      const blob = await api.deletionDownload(e.id, variant);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = variant === "json" ? "json" : "pdf";
      a.download = `${(e.title || e.template_title || "deleted").replace(/\s+/g, "_")}_deleted.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.response?.data?.detail || "Download failed."); }
    finally { setDl(""); }
  };

  return (
    <div data-testid="deletions-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] text-slate-400">Deleted documents and document types — with a downloadable copy of what was removed. This is an audit record; deletions can't be restored.</p>
        <Button variant="outline" size="sm" onClick={load} data-testid="deletions-refresh-btn" className="gap-1.5 shrink-0">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>
      {rows === null ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-12 text-center rounded-lg border border-slate-200 bg-white" data-testid="deletions-empty">Nothing deleted yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((e) => (
            <div key={e.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`deletion-row-${e.id}`}>
              <div className="flex items-center gap-1.5">
                <Badge className={`text-[9px] py-0 ${e.kind === "template" ? "bg-purple-50 text-purple-600 border-purple-200" : e.kind === "service_agreement" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{e.kind === "template" ? "Document type" : e.kind === "service_agreement" ? "Service Agreement" : "Document"}</Badge>
                <span className="text-sm font-medium text-slate-800 truncate">{e.title || e.template_title || "—"}</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {e.participant_name ? `${e.participant_name} · ` : ""}{e.workflow_state ? `${e.workflow_state} · ` : ""}deleted by {e.deleted_by} · {fmtLogFull(e.deleted_at)}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(e.available || ["json"]).map((v) => (
                  <Button key={v} size="sm" variant="outline" className="h-7 text-[11px]" disabled={dl === `${e.id}-${v}`}
                    onClick={() => download(e, v)} data-testid={`deletion-dl-${e.id}-${v}`}>
                    {dl === `${e.id}-${v}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}{VARIANT_LABEL[v] || v}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



const PHASE_LABEL = {
  geolocation: "Location / GPS",
  validation: "Rejected by system",
  geofence: "Geofence",
  clocked: "Recorded",
};

function ClockEvents({ active }) {
  const [rows, setRows] = useState(null);
  const [errorsOnly, setErrorsOnly] = useState(true);
  const [direction, setDirection] = useState("all");
  const [worker, setWorker] = useState("");

  const load = useCallback(() => {
    setRows(null);
    const params = { limit: 300, errors_only: errorsOnly };
    if (direction !== "all") params.direction = direction;
    if (worker.trim()) params.worker = worker.trim();
    api.clockEventLog(params).then((d) => setRows(d.entries || []))
      .catch(() => { setRows([]); toast.error("Could not load clock events."); });
  }, [errorsOnly, direction, worker]);

  useEffect(() => { if (active) load(); }, [active, load]);

  return (
    <div data-testid="clock-events-panel">
      <p className="text-[11px] text-slate-400 mb-3">Every clock in/out attempt — successful or not — captured from both the worker's phone and the server, with the reason behind each failure (location denied, GPS timeout, out of range, already clocked in, and more). Use this to investigate why a worker couldn't clock on.</p>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg bg-white border border-slate-200">
        <div className="w-40">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Show</label>
          <Select value={errorsOnly ? "errors" : "all"} onValueChange={(v) => setErrorsOnly(v === "errors")}>
            <SelectTrigger data-testid="clock-filter-outcome" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="errors">Failures only</SelectItem>
              <SelectItem value="all">All events</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Direction</label>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger data-testid="clock-filter-direction" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">In &amp; out</SelectItem>
              <SelectItem value="in">Clock in</SelectItem>
              <SelectItem value="out">Clock out</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Worker</label>
          <Input data-testid="clock-filter-worker" className="h-9 mt-1" placeholder="name / email"
                 value={worker} onChange={(e) => setWorker(e.target.value)} />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="clock-refresh-btn" className="gap-1.5 ml-auto">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {rows === null ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-12 text-center rounded-lg border border-slate-200 bg-white" data-testid="clock-events-empty">No clock events match these filters.</div>
      ) : (
        <div className="space-y-2" data-testid="clock-events-list">
          {rows.map((e) => {
            const isError = e.outcome === "error";
            const DirIcon = e.direction === "out" ? LogOut : LogIn;
            return (
              <div key={e.id} className={`rounded-lg border p-3 ${isError ? "border-rose-200 bg-rose-50/50" : "border-slate-200 bg-white"}`} data-testid={`clock-event-${e.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${e.direction === "out" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                    <DirIcon className="h-3 w-3" />Clock {e.direction === "out" ? "out" : "in"}
                  </span>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isError ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                    {isError ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{isError ? "Failed" : "Success"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50">
                    {e.source === "client" ? <Smartphone className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}{e.source === "client" ? "Worker device" : "Server"}
                  </span>
                  <span className="text-[10px] text-slate-400">· {PHASE_LABEL[e.phase] || e.phase}{e.error_code ? ` (${e.error_code})` : ""}</span>
                  <span className="text-[10px] text-slate-400 ml-auto">{fmt(e.at)}</span>
                </div>
                <div className="text-sm font-medium text-slate-800 mt-1.5">{e.worker_name || e.worker}</div>
                <div className={`text-[12px] mt-0.5 ${isError ? "text-rose-700" : "text-slate-600"}`}>{e.reason || "—"}</div>
                <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 items-center">
                  {e.shift && <span className="font-mono">{e.shift}</span>}
                  {(e.latitude != null && e.longitude != null) && <span>{Number(e.latitude).toFixed(5)},{Number(e.longitude).toFixed(5)}{e.accuracy != null ? ` · ±${Math.round(e.accuracy)}m` : ""}</span>}
                  {e.out_of_range && <span className="inline-flex items-center gap-0.5 text-orange-600 font-semibold"><MapPinOff className="h-3 w-3" />Out of range</span>}
                  {e.user_agent && <span className="truncate max-w-[280px]" title={e.user_agent}>{e.user_agent}</span>}
                </div>
                {e.diagnostics && (
                  <div className="text-[10px] font-mono text-slate-500 mt-1 break-all" data-testid={`clock-event-diag-${e.id}`}>
                    {Object.entries(e.diagnostics).map(([k, v]) => `${k}=${v === null ? "null" : String(v)}`).join(" · ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {rows && rows.length > 0 && <div className="mt-2 text-[11px] text-slate-400">Showing {rows.length} most recent events.</div>}
    </div>
  );
}

// ── Email log key → human-friendly label map (mirrors backend REGISTRY labels) ──
const EMAIL_KEY_LABELS = {
  "all": "All email types",
  "twofa_code": "Verification code (2FA)",
  "password_reset": "Set / reset password link",
  "welcome_staff": "Welcome email",
  "portal_access": "Portal access invite",
  "doc_signing_invite": "Document signing invite",
  "sa_signing_invite": "Service agreement signing invite",
  "incident_alert": "Incident manager alert",
  "shift_published": "Shift published notification",
  "timesheet_submit": "Timesheet submission reminder",
  "notes_returned": "Shift notes sent back",
  "compliance_expiry": "Compliance expiry warning",
  "compliance_overdue": "Compliance overdue notice",
};

const EMAIL_STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  skipped: "bg-slate-50 text-slate-500 border-slate-200",
};

const EMAIL_STATUS_ICON = {
  sent: <CheckCircle2 className="h-3 w-3" />,
  failed: <XCircle className="h-3 w-3" />,
  skipped: <MinusCircle className="h-3 w-3" />,
};

function EmailsSent({ active }) {
  const [rows, setRows] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "all", key: "all", date_from: "", date_to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.key !== "all") params.key = filters.key;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [logData, healthData] = await Promise.all([
        api.emailLogFull(params),
        api.emailHealth(7),
      ]);
      setRows(logData.log || []);
      setHealth(healthData);
    } catch {
      toast.error("Couldn't load email log.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const healthFailed = health?.failed ?? 0;

  return (
    <div data-testid="emails-sent-tab">
      {health && (
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 min-w-[120px]">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <div>
              <div className="text-lg font-bold text-emerald-700">{health.sent}</div>
              <div className="text-[10px] text-emerald-600 font-medium">Sent (7d)</div>
            </div>
          </div>
          <div className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 min-w-[120px] ${healthFailed > 0 ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`}>
            <XCircle className={`h-4 w-4 shrink-0 ${healthFailed > 0 ? "text-rose-600" : "text-slate-400"}`} />
            <div>
              <div className={`text-lg font-bold ${healthFailed > 0 ? "text-rose-700" : "text-slate-500"}`}>{health.failed}</div>
              <div className={`text-[10px] font-medium ${healthFailed > 0 ? "text-rose-600" : "text-slate-400"}`}>Failed (7d)</div>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 min-w-[120px]">
            <MinusCircle className="h-4 w-4 text-slate-400 shrink-0" />
            <div>
              <div className="text-lg font-bold text-slate-500">{health.skipped}</div>
              <div className="text-[10px] text-slate-400 font-medium">Skipped (7d)</div>
            </div>
          </div>
          {health.recent_failures?.length > 0 && (
            <div className="flex-1 min-w-[280px] rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-600 mb-1.5">Recent failures</div>
              <div className="space-y-1">
                {health.recent_failures.map((f, i) => (
                  <div key={i} className="text-[11px] text-rose-700">
                    <span className="font-medium">{EMAIL_KEY_LABELS[f.label] || f.label}</span>
                    <span className="text-rose-400 mx-1">→</span>
                    <span className="font-mono">{f.to}</span>
                    {f.detail && <span className="text-rose-500 ml-1">— {f.detail}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg bg-white border border-slate-200">
        <div className="w-44">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</label>
          <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="h-9 mt-1" data-testid="email-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Email type</label>
          <Select value={filters.key} onValueChange={(v) => setFilters((f) => ({ ...f, key: v }))}>
            <SelectTrigger className="h-9 mt-1" data-testid="email-filter-key"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(EMAIL_KEY_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</label>
          <Input type="date" className="h-9 mt-1" data-testid="email-filter-from"
                 value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} />
        </div>
        <div className="w-40">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</label>
          <Input type="date" className="h-9 mt-1" data-testid="email-filter-to"
                 value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} />
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 ml-auto" data-testid="email-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows === null ? null : rows.length === 0 ? (
        <div className="text-sm text-slate-400 py-12 text-center rounded-lg border border-slate-200 bg-white" data-testid="email-log-empty">
          No emails match these filters.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="email-log-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                <th className="text-left font-semibold px-4 py-2.5">When</th>
                <th className="text-left font-semibold px-4 py-2.5">Email type</th>
                <th className="text-left font-semibold px-4 py-2.5">Recipient</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Detail / error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isFailed = r.status === "failed";
                return (
                  <tr key={i} className={`border-b border-slate-100 ${isFailed ? "bg-rose-50/40" : ""}`} data-testid="email-log-row">
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmt(r.at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3 w-3 text-slate-400 shrink-0" />
                        <span className="text-slate-700 font-medium">{r.label || EMAIL_KEY_LABELS[r.key] || r.key}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 font-mono text-[11px]">{r.to || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${EMAIL_STATUS_STYLE[r.status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
                        {EMAIL_STATUS_ICON[r.status]}
                        {r.status}
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 max-w-[320px] truncate ${isFailed ? "text-rose-700 font-medium" : "text-slate-400"}`} title={r.detail}>
                      {r.detail || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">
            Showing {rows.length} most recent entries.
          </div>
        </div>
      )}
    </div>
  );
}



// --------------------------------------------------------- archive card --
function ArchiveStatus({ active }) {
  const [st, setSt] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (active) api.logArchiveStatus().then(setSt).catch(() => setSt({ error: true }));
  }, [active]);

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.logArchiveTest();
      if (r.ok) toast.success("Test file uploaded: the off-server copy is working.");
      else toast.error(r.error || "The test upload failed.");
    } catch (e) { toast.error(e.response?.data?.detail || "The test upload failed."); }
    finally { setTesting(false); api.logArchiveStatus().then(setSt).catch(() => {}); }
  };

  if (!st || st.error) return null;
  const failing = st.consecutive_failures > 0;
  const tone = !st.configured ? "border-amber-200 bg-amber-50" : failing ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50";
  return (
    <div className={`rounded-lg border p-3 mb-3 flex items-start justify-between gap-3 ${tone}`} data-testid="archive-status">
      <div className="text-xs text-slate-700">
        <div className="font-semibold">Off-server copy</div>
        {!st.configured ? (
          <div className="mt-0.5">Not set up on this server, so these logs exist only here and are never trimmed. See deploy/LOG-ARCHIVE.md.</div>
        ) : (
          <div className="mt-0.5 space-y-0.5">
            <div>
              Copied hourly to locked storage in Sydney (kept {Math.round(st.retain_days / 365)} years; web server logs {Math.round(st.weblog_retain_days / 30)} months).
              The app keeps about {Math.round(st.keep_in_app_days / 30)} months.
            </div>
            <div>Last copied: {st.last_success_at ? fmtLogFull(st.last_success_at) : "not yet"}</div>
            {failing && <div className="text-red-700">Failing ({st.consecutive_failures}×) since {fmtLogFull(st.last_error_at)}: {st.last_error}. Nothing is trimmed until it works again.</div>}
          </div>
        )}
      </div>
      {st.configured && (
        <Button variant="outline" size="sm" onClick={test} disabled={testing} data-testid="archive-test-btn" className="shrink-0 bg-white">
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Test upload
        </Button>
      )}
    </div>
  );
}

// --------------------------------------------------------------- security --
const SEC_ACTION = {
  login: "Sign-in",
  login_password_ok: "Password accepted, 2FA next",
  account_locked: "Account locked",
  mfa: "2FA code",
  mfa_code_resent: "2FA code resent",
  mfa_enrolment_started: "2FA setup started",
  mfa_enrolled: "2FA set up",
  mfa_reset: "2FA reset by admin",
  session_rejected: "Invalid session token",
  access_denied: "Access denied",
  password_changed: "Password changed",
  password_reset: "Password reset (link)",
  password_reset_requested: "Forgot password request",
  password_reset_sent: "Set-password link sent by admin",
  password_setup_sent: "Set-password link sent (first sign-in)",
  user_created: "Login created",
  user_updated: "Login changed",
  user_roles_changed: "Roles changed",
  user_deleted: "Login deleted",
  user_linked: "Login linked to HR profile",
  user_unlinked: "Login unlinked from HR profile",
  welcome_resent: "Welcome email resent",
  users_seeded: "Logins imported",
  auth_source_set: "Sign-in method set",
  device_trusted: "Device trusted (skip 2FA)",
  device_trust_revoked: "Trusted device removed",
  audit_log_viewed: "Audit log viewed",
  audit_entry_viewed: "Audit entry opened",
  security_log_viewed: "Security log viewed",
  access_log_viewed: "Record access log viewed",
  write_log_viewed: "Write log viewed",
  logout: "Signed out",
  sessions_ended: "Signed out everywhere",
  signing_link_verify: "Signing link identity check",
  xero_connected: "Xero connected",
  xero_disconnected: "Xero disconnected",
  scheduled_job_run: "Scheduled job run by hand",
  log_archive_tested: "Off-server copy tested",
  security_alerts_viewed: "Security alerts viewed",
  security_alert_test: "Test security alert sent",
};

const SEC_REASON = {
  bad_password: "Wrong password",
  unknown_user: "No such login",
  disabled: "Login disabled",
  locked: "Locked after too many attempts",
  bad_code: "Wrong code",
  too_many_attempts: "Too many wrong codes",
  challenge_invalid: "Code session expired",
  invalid_token: "Altered or foreign token",
  no_role: "No role set up",
  no_staff_profile: "No staff profile linked",
  account_unavailable: "Login disabled or removed",
  rate_limited: "Too many requests",
  invalid_link: "Invalid link",
  expired_link: "Expired link",
  used_link: "Link already used",
  bad_current_password: "Wrong current password",
  no_password_yet: "No password set yet",
  sent_by_admin: "Sent by an admin",
  login_deleted: "Login deleted",
  login_disabled: "Login disabled",
  signed_out: "Session was signed out",
  signed_out_everywhere: "Signed out everywhere earlier",
  by_user: "By the person themselves",
  by_admin: "By an admin",
  mfa_reset: "2FA was reset",
  password_changed: "Password changed",
  password_reset: "Password reset",
  portal_access_revoked: "Portal access removed",
};

const OUTCOME_STYLE = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failure: "bg-red-50 text-red-700 border-red-200",
  denied: "bg-amber-50 text-amber-700 border-amber-200",
};

function secDetail(e) {
  const parts = [];
  if (e.reason) parts.push(e.action === "access_denied" ? `Needs ${e.reason}` : (SEC_REASON[e.reason] || e.reason));
  if (e.target) parts.push(e.target);
  const d = e.detail || {};
  Object.entries(d).forEach(([k, v]) => {
    if (v && typeof v === "object" && "before" in v) {
      const show = (x) => (Array.isArray(x) ? x.join(", ") || "none" : String(x ?? "—"));
      parts.push(`${k}: ${show(v.before)} → ${show(v.after)}`);
    } else if (v !== null && v !== undefined && v !== "") {
      parts.push(`${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`);
    }
  });
  return parts.join(" · ");
}

const SEC_EMPTY = { actor: "", action: "", outcome: "all", ip: "", date_from: "", date_to: "" };

function SecurityEvents({ active }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(SEC_EMPTY);
  const [filters, setFilters] = useState(SEC_EMPTY);

  const load = useCallback(() => {
    setRows(null);
    const params = { limit: 300 };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.securityLog(params).then((d) => setRows(d.entries || []))
      .catch(() => { setRows([]); toast.error("Could not load the security log."); });
  }, [filters]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));
  const onKeyDown = (e) => { if (e.key === "Enter") setFilters({ ...draft }); };

  return (
    <div data-testid="security-panel">
      <ArchiveStatus active={active} />
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-[11px] text-slate-400 flex items-start gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-px" />
          Sign-ins and failed attempts, 2FA, password and login changes, refused access and who viewed these logs, with the
          IP address and device. Use it to check what an account did if you think it was misused. Viewing this log is itself recorded.
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="security-refresh-btn" className="gap-1.5 shrink-0">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <label className="text-[11px] text-slate-500 col-span-2 md:col-span-1">User
          <Input data-testid="security-filter-actor" className="h-9 mt-1" placeholder="partial email"
            value={draft.actor} onChange={set("actor")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Event
          <Select value={draft.action || "all"} onValueChange={(v) => setDraft((d) => ({ ...d, action: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="security-filter-action" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {Object.entries(SEC_ACTION).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="text-[11px] text-slate-500">Result
          <Select value={draft.outcome} onValueChange={set("outcome")}>
            <SelectTrigger data-testid="security-filter-outcome" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="success">Succeeded</SelectItem>
              <SelectItem value="failure">Failed</SelectItem>
              <SelectItem value="denied">Refused</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="text-[11px] text-slate-500">IP address
          <Input data-testid="security-filter-ip" className="h-9 mt-1" placeholder="exact IP"
            value={draft.ip} onChange={set("ip")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">From
          <Input type="date" data-testid="security-filter-from" className="h-9 mt-1" value={draft.date_from} onChange={set("date_from")} />
        </label>
        <label className="text-[11px] text-slate-500">To
          <Input type="date" data-testid="security-filter-to" className="h-9 mt-1" value={draft.date_to} onChange={set("date_to")} />
        </label>
        <div className="col-span-2 md:col-span-6 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { setDraft(SEC_EMPTY); setFilters(SEC_EMPTY); }} data-testid="security-clear-btn">Clear</Button>
          <Button size="sm" onClick={() => setFilters({ ...draft })} data-testid="security-apply-btn" style={{ backgroundColor: CRIMSON, color: "#fff" }}>Apply</Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <th className="text-left font-semibold px-3 py-2.5">When</th>
              <th className="text-left font-semibold px-3 py-2.5">User</th>
              <th className="text-left font-semibold px-3 py-2.5">Event</th>
              <th className="text-left font-semibold px-3 py-2.5">Result</th>
              <th className="text-left font-semibold px-3 py-2.5">IP address</th>
              <th className="text-left font-semibold px-3 py-2.5">Details</th>
            </tr>
          </thead>
          <tbody data-testid="security-rows">
            {rows === null ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="security-empty">No security events match these filters.</td></tr>
            ) : rows.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 align-top" data-testid="security-row">
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtLogFull(e.ts)}</td>
                <td className="px-3 py-2 text-slate-800 font-medium whitespace-nowrap">{e.actor || "—"}</td>
                <td className="px-3 py-2 text-slate-700">{SEC_ACTION[e.action] || e.action}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${OUTCOME_STYLE[e.outcome] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {e.outcome === "success" ? "Succeeded" : e.outcome === "denied" ? "Refused" : "Failed"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{e.ip || "—"}{countryTag(e.country)}</td>
                <td className="px-3 py-2 text-slate-500">
                  <div>{secDetail(e) || <span className="text-slate-300">—</span>}</div>
                  {e.user_agent && <div className="text-[10px] text-slate-400 truncate max-w-xs" title={e.user_agent}>{e.user_agent}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && rows.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-400">Showing {rows.length} most recent events.</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------- record access --
const RECORD_TYPE = {
  participant: "Participant",
  staff: "Staff",
  incident: "Incident",
  document: "Document",
  service_agreement: "Service agreement",
  shift: "Shift",
  shift_note: "Shift notes",
  timesheet: "Timesheets",
  contact: "Contacts",
  payroll_run: "Payroll run",
  invoice_run: "Invoice run",
  deleted_document: "Deleted document",
};

const KIND_LABEL = { view: "Viewed", list: "Listed", export: "Exported", download: "Downloaded" };
const KIND_STYLE = {
  view: "bg-slate-50 text-slate-600 border-slate-200",
  list: "bg-violet-50 text-violet-700 border-violet-200",
  export: "bg-amber-50 text-amber-700 border-amber-200",
  download: "bg-sky-50 text-sky-700 border-sky-200",
};

// The part of a record that was opened: "/api/participants/{name}/documents" ->
// "documents", "/api/hr/staff/{name}" -> "details", "/api/hr/export/csv" -> "hr / export / csv".
function accessSection(route) {
  const r = route || "";
  const tidy = (segs) => segs.join(" / ").replace(/-/g, " ");
  const i = r.indexOf("{");
  if (i < 0) return tidy(r.replace(/^\/api\//, "").split("/").filter(Boolean));
  const after = r.slice(i).split("/").slice(1).filter((s) => s && !s.startsWith("{"));
  return after.length ? tidy(after) : "details";
}

function fmtBytes(n) {
  if (!n) return "";
  return n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
}

const ACCESS_EMPTY = { actor: "", resource_type: "all", resource_id: "", kind: "all", date_from: "", date_to: "" };

function RecordAccess({ active }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(ACCESS_EMPTY);
  const [filters, setFilters] = useState(ACCESS_EMPTY);

  const load = useCallback(() => {
    setRows(null);
    const params = { limit: 300 };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.accessLog(params).then((d) => setRows(d.entries || []))
      .catch(() => { setRows([]); toast.error("Could not load the record access log."); });
  }, [filters]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));
  const onKeyDown = (e) => { if (e.key === "Enter") setFilters({ ...draft }); };

  return (
    <div data-testid="access-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-[11px] text-slate-400">
          Who opened, listed, exported or downloaded participant, staff, incident, shift, timesheet, document and service
          agreement records, with the IP address. Lists (such as the roster) show the filters used, like the date range, and
          how much was loaded. Viewing this log is itself recorded.
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="access-refresh-btn" className="gap-1.5 shrink-0">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <label className="text-[11px] text-slate-500 col-span-2 md:col-span-1">User
          <Input data-testid="access-filter-actor" className="h-9 mt-1" placeholder="partial email"
            value={draft.actor} onChange={set("actor")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Record type
          <Select value={draft.resource_type} onValueChange={set("resource_type")}>
            <SelectTrigger data-testid="access-filter-type" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(RECORD_TYPE).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="text-[11px] text-slate-500">Record ID
          <Input data-testid="access-filter-id" className="h-9 mt-1" placeholder="e.g. PAR00001"
            value={draft.resource_id} onChange={set("resource_id")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Action
          <Select value={draft.kind} onValueChange={set("kind")}>
            <SelectTrigger data-testid="access-filter-kind" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="view">Viewed</SelectItem>
              <SelectItem value="list">Listed</SelectItem>
              <SelectItem value="export">Exported</SelectItem>
              <SelectItem value="download">Downloaded</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="text-[11px] text-slate-500">From
          <Input type="date" data-testid="access-filter-from" className="h-9 mt-1" value={draft.date_from} onChange={set("date_from")} />
        </label>
        <label className="text-[11px] text-slate-500">To
          <Input type="date" data-testid="access-filter-to" className="h-9 mt-1" value={draft.date_to} onChange={set("date_to")} />
        </label>
        <div className="col-span-2 md:col-span-6 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { setDraft(ACCESS_EMPTY); setFilters(ACCESS_EMPTY); }} data-testid="access-clear-btn">Clear</Button>
          <Button size="sm" onClick={() => setFilters({ ...draft })} data-testid="access-apply-btn" style={{ backgroundColor: CRIMSON, color: "#fff" }}>Apply</Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <th className="text-left font-semibold px-3 py-2.5">When</th>
              <th className="text-left font-semibold px-3 py-2.5">User</th>
              <th className="text-left font-semibold px-3 py-2.5">Action</th>
              <th className="text-left font-semibold px-3 py-2.5">Record</th>
              <th className="text-left font-semibold px-3 py-2.5">Part</th>
              <th className="text-left font-semibold px-3 py-2.5">IP address</th>
            </tr>
          </thead>
          <tbody data-testid="access-rows">
            {rows === null ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="access-empty">No record access matches these filters.</td></tr>
            ) : rows.map((e) => (
              <tr key={e.id} className="border-b border-slate-100 align-top" data-testid="access-row">
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtLogFull(e.ts)}</td>
                <td className="px-3 py-2 text-slate-800 font-medium whitespace-nowrap">{e.actor}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${KIND_STYLE[e.kind] || KIND_STYLE.view}`}>
                    {KIND_LABEL[e.kind] || e.kind}
                  </span>
                  {e.kind !== "view" && e.bytes > 0 && <div className="text-[10px] text-slate-400 mt-0.5">{fmtBytes(e.bytes)}</div>}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  <div>{RECORD_TYPE[e.resource_type] || e.resource_type}{e.resource_name ? `: ${e.resource_name}` : ""}</div>
                  {e.resource_id && <div className="font-mono text-[10px] text-slate-400">{e.resource_id}</div>}
                  {!e.resource_id && e.query && <div className="text-[10px] text-slate-400">{Object.entries(e.query).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>}
                </td>
                <td className="px-3 py-2 text-slate-500 capitalize">{accessSection(e.route)}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{e.ip || "—"}{countryTag(e.country)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && rows.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-400">Showing {rows.length} most recent entries.</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------- writes --
const WRITE_OP = { create: "Created", update: "Changed", delete: "Deleted" };
const WRITE_STYLE = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-sky-50 text-sky-700 border-sky-200",
  delete: "bg-red-50 text-red-700 border-red-200",
};
const WRITES_EMPTY = { actor: "", collection: "", record_id: "", op: "all", date_from: "", date_to: "" };

function WriteReceipts({ active }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState(WRITES_EMPTY);
  const [filters, setFilters] = useState(WRITES_EMPTY);

  const load = useCallback(() => {
    setRows(null);
    const params = { limit: 300 };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v.trim ? v.trim() : v; });
    api.writeLog(params).then((d) => setRows(d.entries || []))
      .catch(() => { setRows([]); toast.error("Could not load the write log."); });
  }, [filters]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));
  const onKeyDown = (e) => { if (e.key === "Enter") setFilters({ ...draft }); };

  return (
    <div data-testid="writes-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-[11px] text-slate-400">
          A receipt for every change saved to the database while someone used the app: who, when, the IP address, which
          records and which fields. Values aren&apos;t kept here; the Activity tab shows before and after where they matter.
          Use it to check nothing was changed that the Activity tab doesn&apos;t show. Viewing this log is itself recorded.
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="writes-refresh-btn" className="gap-1.5 shrink-0">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <label className="text-[11px] text-slate-500 col-span-2 md:col-span-1">User
          <Input data-testid="writes-filter-actor" className="h-9 mt-1" placeholder="partial email"
            value={draft.actor} onChange={set("actor")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Collection
          <Input data-testid="writes-filter-collection" className="h-9 mt-1" placeholder="e.g. participants"
            value={draft.collection} onChange={set("collection")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Record ID
          <Input data-testid="writes-filter-id" className="h-9 mt-1" placeholder="exact id"
            value={draft.record_id} onChange={set("record_id")} onKeyDown={onKeyDown} />
        </label>
        <label className="text-[11px] text-slate-500">Change
          <Select value={draft.op} onValueChange={set("op")}>
            <SelectTrigger data-testid="writes-filter-op" className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="create">Created</SelectItem>
              <SelectItem value="update">Changed</SelectItem>
              <SelectItem value="delete">Deleted</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="text-[11px] text-slate-500">From
          <Input type="date" data-testid="writes-filter-from" className="h-9 mt-1" value={draft.date_from} onChange={set("date_from")} />
        </label>
        <label className="text-[11px] text-slate-500">To
          <Input type="date" data-testid="writes-filter-to" className="h-9 mt-1" value={draft.date_to} onChange={set("date_to")} />
        </label>
        <div className="col-span-2 md:col-span-6 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { setDraft(WRITES_EMPTY); setFilters(WRITES_EMPTY); }} data-testid="writes-clear-btn">Clear</Button>
          <Button size="sm" onClick={() => setFilters({ ...draft })} data-testid="writes-apply-btn" style={{ backgroundColor: CRIMSON, color: "#fff" }}>Apply</Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <th className="text-left font-semibold px-3 py-2.5">When</th>
              <th className="text-left font-semibold px-3 py-2.5">User</th>
              <th className="text-left font-semibold px-3 py-2.5">Change</th>
              <th className="text-left font-semibold px-3 py-2.5">Collection and records</th>
              <th className="text-left font-semibold px-3 py-2.5">Fields</th>
              <th className="text-left font-semibold px-3 py-2.5">IP address</th>
            </tr>
          </thead>
          <tbody data-testid="writes-rows">
            {rows === null ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="writes-empty">No writes match these filters.</td></tr>
            ) : rows.map((e, i) => (
              <tr key={`${e.request_id}-${i}`} className="border-b border-slate-100 align-top" data-testid="writes-row">
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtLogFull(e.ts)}</td>
                <td className="px-3 py-2 text-slate-800 font-medium whitespace-nowrap">{e.actor || "Not signed in"}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${WRITE_STYLE[e.op] || ""}`}>
                    {WRITE_OP[e.op] || e.op}
                  </span>
                  {e.many && e.count != null && <div className="text-[10px] text-slate-400 mt-0.5">{e.count} record{e.count === 1 ? "" : "s"}</div>}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  <div className="font-mono text-[11px]">{e.collection}</div>
                  {(e.ids || []).length > 0 && <div className="font-mono text-[10px] text-slate-400 break-all">{e.ids.slice(0, 5).join(", ")}{e.ids.length > 5 ? ` +${e.ids.length - 5}` : ""}</div>}
                  {!(e.ids || []).length && e.matched_on && <div className="text-[10px] text-slate-400">matched on {e.matched_on.join(", ")}</div>}
                </td>
                <td className="px-3 py-2 text-slate-500 text-[11px]">{(e.fields || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{e.ip || "—"}{countryTag(e.country)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && rows.length > 0 && (
        <div className="mt-2 text-[11px] text-slate-400">Showing {rows.length} most recent writes.</div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------- alerts --
// Country next to an IP address (ISO code from the on-server lookup).
function countryTag(c) {
  if (!c || c === "private") return null;
  return <span className={`ml-1.5 font-sans text-[10px] font-semibold ${c === "AU" ? "text-slate-400" : "text-red-600"}`}>{c}</span>;
}

const RULE_LABEL = {
  failed_signins: "Failed sign-ins",
  foreign_signin: "Sign-in from overseas",
  admin_change: "Admin change",
  large_export: "Large export",
  signing_link: "Signing link",
  probing: "Refused requests",
  archive_failing: "Off-server copy",
};

function AlertsPanel({ active }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAcked, setShowAcked] = useState(false);

  const load = useCallback(() => {
    api.securityAlerts().then((d) => {
      setData(d);
      setForm({ ...d.settings, allowed_countries: (d.settings.allowed_countries || []).join(", ") });
    }).catch(() => { setData({ alerts: [] }); toast.error("Could not load security alerts."); });
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, allowed_countries: form.allowed_countries.split(",").map((c) => c.trim()).filter(Boolean) };
      ["failed_signins_per_account", "failed_signins_total", "export_mb", "exports_per_hour"].forEach((k) => { body[k] = Number(body[k]); });
      await api.saveSecurityAlertSettings(body);
      toast.success("Alert settings saved.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save the alert settings."); }
    finally { setSaving(false); }
  };

  const test = async () => {
    try {
      const r = await api.testSecurityAlert();
      const texts = r.texted ? ` and texted to ${r.texted} mobile${r.texted === 1 ? "" : "s"}` : "";
      toast.success(`Test alert sent to ${r.sent_to}${texts}.`);
      if (r.texts_failed) toast.error(`${r.texts_failed} text${r.texts_failed === 1 ? "" : "s"} couldn't be sent. Check Office → SMS → Log.`);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the test alert."); }
  };

  const ack = async (a) => {
    const note = window.prompt("Acknowledge this alert. Note (optional), e.g. what you checked:", "");
    if (note === null) return;
    try { await api.ackSecurityAlert(a.id, note); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't acknowledge the alert."); }
  };

  if (!data || !form) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const alerts = (data.alerts || []).filter((a) => showAcked || !a.acknowledged);
  const num = (k, label, hint) => (
    <label className="text-[11px] text-slate-500">{label}
      <Input type="number" min={1} className="h-9 mt-1" value={form[k]} onChange={set(k)} data-testid={`alerts-${k}`} />
      <span className="text-[10px] text-slate-400">{hint}</span>
    </label>
  );

  return (
    <div data-testid="alerts-panel" className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-sm font-semibold text-slate-800">Alert emails and texts</div>
            <p className="text-[11px] text-slate-500">Checked every 5 minutes. Alerts always appear below; emails and texts go only when switched on.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 shrink-0">
            <Switch checked={!!form.enabled} onCheckedChange={set("enabled")} data-testid="alerts-enabled" /> Send alerts
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <label className="text-[11px] text-slate-500 md:col-span-2">Send to (comma-separated)
            <Input className="h-9 mt-1" placeholder="e.g. security@prydessupport.com" value={form.recipients} onChange={set("recipients")} data-testid="alerts-recipients" />
          </label>
          <label className="text-[11px] text-slate-500">Expected countries
            <Input className="h-9 mt-1" placeholder="AU" value={form.allowed_countries} onChange={set("allowed_countries")} data-testid="alerts-countries" />
            <span className="text-[10px] text-slate-400">Two-letter codes, e.g. AU, NZ</span>
          </label>
          <label className="text-[11px] text-slate-500 md:col-span-2">Text to mobiles (comma-separated, optional)
            <Input className="h-9 mt-1" placeholder="e.g. 0412 345 678, 0498 765 432" value={form.sms_recipients || ""} onChange={set("sms_recipients")} data-testid="alerts-sms-recipients" />
            <span className="text-[10px] text-slate-400">Sent with the SMS account in Settings → Messaging</span>
          </label>
          <label className="flex items-center gap-2 text-[11px] text-slate-500 md:pt-5">
            <Switch checked={!!form.sms_all} onCheckedChange={set("sms_all")} data-testid="alerts-sms-all" />
            <span>{form.sms_all ? "Text every alert" : "Text high alerts only"}</span>
          </label>
          {num("failed_signins_per_account", "Failed sign-ins, one account", "in 15 minutes")}
          {num("failed_signins_total", "Failed sign-ins, all accounts", "in 15 minutes")}
          {num("export_mb", "Large export (MB)", "one export, download or list")}
          {num("exports_per_hour", "Exports per hour, one person", "exports and downloads")}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <span className="text-[10px] text-slate-400">{data.geoip_attribution}. Countries are looked up on this server.</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={test} data-testid="alerts-test-btn">Send test alert</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="alerts-save-btn" style={{ backgroundColor: CRIMSON, color: "#fff" }}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Save
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-slate-500">
          <Switch checked={showAcked} onCheckedChange={setShowAcked} data-testid="alerts-show-acked" /> Show acknowledged
        </label>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="alerts-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {alerts.length === 0 ? (
        <div className="text-sm text-slate-400 py-12 text-center rounded-lg border border-slate-200 bg-white" data-testid="alerts-empty">
          {showAcked ? "No alerts yet." : "Nothing needs looking at."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="alerts-list">
          {alerts.map((a) => (
            <div key={a.id} className={`rounded-lg border bg-white p-3 ${a.severity === "high" && !a.acknowledged ? "border-red-200" : "border-slate-200"}`} data-testid="alert-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-semibold ${a.severity === "high" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {a.severity === "high" ? "High" : "Medium"}
                    </span>
                    <span className="text-[11px] text-slate-500">{RULE_LABEL[a.rule] || a.rule}</span>
                    {a.repeats > 0 && <span className="text-[11px] text-slate-400">· seen {a.repeats + 1}×</span>}
                    {a.emailed && <span className="text-[11px] text-slate-400">· emailed</span>}
                  </div>
                  <div className="text-sm text-slate-800 mt-1">{a.summary}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {fmtLogFull(a.ts)}
                    {a.details?.ip ? ` · IP ${a.details.ip}` : ""}{a.details?.country ? ` (${a.details.country})` : ""}
                    {a.details?.ips ? ` · IPs ${a.details.ips.join(", ")}` : ""}
                  </div>
                  {a.acknowledged && (
                    <div className="text-[11px] text-emerald-700 mt-1">
                      Acknowledged by {a.acknowledged.name} {fmtLogFull(a.acknowledged.at)}{a.acknowledged.note ? `: ${a.acknowledged.note}` : ""}
                    </div>
                  )}
                </div>
                {!a.acknowledged && (
                  <Button variant="outline" size="sm" onClick={() => ack(a)} className="shrink-0" data-testid={`alert-ack-${a.id}`}>Acknowledge</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
